import { isAfter, isEqual } from 'date-fns'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetPelaksanaanConfig,
  useAPIPelaksanaanSchools,
} from 'api/kinerja/useAPIKinerja'
import {
  isKsDefinitifPlt,
  isUserHasKsRole,
} from 'app/KinerjaKSPage/utils/profile'
import { BannerPltConfirmation } from 'app/KinerjaPage/components/BannerPltConfirmation'
import CardKinerjaPeriksa from 'app/KinerjaPage/components/CardKinerjaPeriksa'
import { DialogPltKSConfirmation } from 'app/KinerjaPage/components/DialogPltKSConfirmation'
import { DialogSelectSchoolPerencanaan } from 'app/KinerjaPage/components/DialogSelectSchoolPerencanaan'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import CardTahapPenilaian from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardTahapPenilaian'
import CardKSStartPeriod from 'app/KinerjaPage/components/CardKSStartPeriod'
import CardKinerjaPeriksaMultipleSchool from '../CardKinerjaPeriksaMultipleSchool'
import { CardSchool } from '../CardSchool'
import { isDateValid } from 'utils/datetime'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  getPelaksanaanDate,
  isDateStarted,
} from 'app/KinerjaShared/utils/pelaksanaan'
import { BannerPelaksanaanNotStarted } from 'app/KinerjaPage/components/BannerPelaksanaanNotStarted'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'

import type { KinerjaProfil } from 'types/kinerja'

interface KepalaSekolahRootViewProps {
  isLoading?: boolean
  profile: KinerjaProfil
  isKinerjaPelaksanaanEnabled?: boolean
}

export default function KepalaSekolahRootView({
  isLoading = false,
  profile,
  isKinerjaPelaksanaanEnabled,
}: KepalaSekolahRootViewProps) {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const { periods, period: periodData, periodNo } = usePeriodContext()

  const [showDialogPerencanaan, setShowDialogPerencanaan] = useState(false)
  const [showDialogPltConfirmation, setShowDialogPltConfirmation] =
    useState(false)

  const { data: dataPelaksanaanSchools, isLoading: isLoadingSchools } =
    useAPIPelaksanaanSchools({
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: dataPelaksanaanConfig } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      // Disable error boundary since the data is not required for this page
      useErrorBoundary: false,
    },
  })

  // Period-related vars
  const isPeriodResolved = Array.isArray(periods) && periods.length > 0
  const pelaksanaanDate = getPelaksanaanDate(periodData.periodeKinerja)
  const isPelaksanaanStarted =
    isPeriodResolved && isDateStarted(periodData.periodeKinerja)
  const period = periodData.period

  // for displaying plt warnings
  const haveUnconfirmedSchools =
    isKsDefinitifPlt(profile) &&
    !!dataPelaksanaanSchools?.data?.find(
      (thisSchool) => thisSchool.confirmationStatus == 'UNCONFIRMED'
    )

  // if user is ks definitif and all school definitif is completed
  const isAllDefinitifSchoolCompleted =
    isUserHasKsRole(profile) &&
    !!dataPelaksanaanSchools?.data
      ?.filter((thisSchool) => thisSchool.role === 'KS_DEFINITIF')
      ?.every((thisSchool) => thisSchool.perencanaanReadyState === 'COMPLETED')

  // check for if is all school perencanaan state is empty (not started at all), if true display list periksa (perencanaan)
  const isAllSchoolEmpty =
    isKsDefinitifPlt(profile) &&
    !!dataPelaksanaanSchools?.data?.every(
      (thisSchool) => thisSchool.perencanaanReadyState == 'EMPTY'
    )

  const RenderBannerPltConfirmation = () =>
    isKsDefinitifPlt(profile) &&
    !isLoadingSchools &&
    haveUnconfirmedSchools && <BannerPltConfirmation />

  const handleTrackLakukanPenilaian = () => {
    trackEvent({
      activity_type: 'penilaian_ks_home_click_lakukan_penilaian',
      screen_name: 'ks_penilaian_home',
      event_trigger: 'ks_penilaian_home',
    })
  }

  // Tahap penilaian
  const showTahapPenilaian = useMemo(() => {
    // access date
    const accessDate =
      dataPelaksanaanConfig?.data?.organizationProgressAccessDate
    const isValidAccessDate = isDateValid(accessDate)
    const currentDate = new Date()
    const accessDateObject = new Date(accessDate)

    return (
      isPelaksanaanStarted &&
      isValidAccessDate &&
      (isAfter(currentDate, accessDateObject) ||
        isEqual(currentDate, accessDateObject))
    )
  }, [
    dataPelaksanaanConfig?.data?.organizationProgressAccessDate,
    isPelaksanaanStarted,
  ])

  const RenderSchools = () => (
    <>
      {dataPelaksanaanSchools?.data
        ?.filter((school) => school.perencanaanReadyState != 'EMPTY')
        .map((school) => (
          <CardSchool
            key={school.npsn}
            baseHref={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja`}
            ctaText="Lakukan Penilaian"
            school={school}
            onClick={() => {
              if (school.confirmationStatus === 'REJECTED') {
                setShowDialogPltConfirmation(true)
              } else {
                handleTrackLakukanPenilaian()
              }
            }}
          />
        ))}

      <DialogPltKSConfirmation
        isOpen={showDialogPltConfirmation}
        onClose={() => setShowDialogPltConfirmation(false)}
      />
    </>
  )

  useEffect(() => {
    if (isKinerjaPelaksanaanEnabled && !isAllSchoolEmpty) {
      trackEvent({
        activity_type: 'penilaian_ks_open_home',
        screen_name: 'ks_penilaian_home',
        event_trigger: 'ks_penilaian_home',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKinerjaPelaksanaanEnabled, isAllSchoolEmpty])

  if (isLoadingSchools)
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner data-testid="loading-spinner" />
      </div>
    )

  if (isKinerjaPelaksanaanEnabled && !isAllSchoolEmpty)
    return (
      <>
        <CardKSStartPeriod
          isMultipleSchoolEnabled
          schools={dataPelaksanaanSchools?.data}
        />
        {!isPelaksanaanStarted && (
          <BannerPelaksanaanNotStarted
            periodNo={periodNo}
            pelaksanaanDate={pelaksanaanDate}
          />
        )}
        {showTahapPenilaian && <CardTahapPenilaian />}
        <RenderBannerPltConfirmation />
        {isUserHasKsRole(profile) && !isAllDefinitifSchoolCompleted && (
          <Card>
            <div className="flex gap-6 p-6">
              <div className="flex-1 flex flex-col gap-1 justify-center">
                <div className="flex items-center gap-2">
                  <Text variant="heading-md">
                    Ada Perencanaan Guru yang belum selesai
                  </Text>
                </div>
                <Text color="subdued">
                  Sebelum lanjut ke tahap Penilaian, selesaikan tahap
                  Perencanaan Guru terlebih dahulu.
                </Text>
              </div>
              <div className="flex flex-col justify-center">
                {isLoading ? (
                  <Shimmer />
                ) : (
                  <Button
                    color="white"
                    as="a"
                    onClick={() => setShowDialogPerencanaan(true)}
                  >
                    Ke Perencanaan Guru <Icon>chevron_right</Icon>
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
        {isPelaksanaanStarted && (
          <Card>
            <div className="flex gap-6 p-6">
              <div className="flex flex-col">
                <Image
                  alt=""
                  src="/illustrations/img-book-rapor-graphic.png"
                  width={84}
                  height={84}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1 justify-center">
                <Text
                  variant="subheading"
                  color="informational"
                  className="!text-blue-60 !uppercase tracking-[0.5px]"
                >
                  <CardPeriodType type="pelaksanaan" />
                </Text>
                <div className="flex items-center gap-2">
                  <Text variant="heading-lg">Penilaian Kinerja</Text>
                  <Badge className="uppercase">Khusus Kepala Sekolah</Badge>
                </div>
                <Text color="subdued">
                  Kumpulkan dokumen dan berikan nilai untuk seluruh guru di
                  satuan pendidikan Anda.
                </Text>
              </div>
            </div>
            <div className="grid grid-cols-2 pt-6 px-6 gap-6">
              {isLoading ? (
                <div className="border border-gray-20 rounded-lg p-6 flex gap-2 justify-between">
                  <Shimmer />
                </div>
              ) : (
                <RenderSchools />
              )}
            </div>

            <div className="rounded bg-cyan-5 p-3 mt-6 flex items-center m-6">
              <Icon color="informational" className="mr-1">
                error
              </Icon>
              Jika satuan pendidikan tidak sesuai, mohon kunjungi&nbsp;
              <a
                className="!text-blue-70 underline"
                href="https://pusatinformasi.guru.kemdikbud.go.id/"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_home_click_pusat_bantuan',
                    screen_name: 'ks_penilaian_home',
                    event_trigger: 'ks_penilaian_home',
                  })
                }}
              >
                Pusat Bantuan
              </a>
              .
            </div>
          </Card>
        )}
        <DialogSelectSchoolPerencanaan
          schools={dataPelaksanaanSchools?.data}
          isOpen={showDialogPerencanaan}
          onClose={() => setShowDialogPerencanaan(false)}
        />
      </>
    )

  if (isAllSchoolEmpty) {
    return (
      <>
        <CardKSStartPeriod
          isMultipleSchoolEnabled
          schools={dataPelaksanaanSchools?.data}
        />
        {showTahapPenilaian && <CardTahapPenilaian />}
        <RenderBannerPltConfirmation />
        <CardKinerjaPeriksaMultipleSchool
          schools={dataPelaksanaanSchools?.data}
          isLoading={isLoading}
        />
      </>
    )
  }

  return (
    <>
      <CardKSStartPeriod
        isMultipleSchoolEnabled
        schools={dataPelaksanaanSchools?.data}
      />
      {showTahapPenilaian && <CardTahapPenilaian />}
      <RenderBannerPltConfirmation />
      <CardKinerjaPeriksa
        period={period}
        profile={profile}
        isLoading={isLoading}
      />
    </>
  )
}
